import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

/** Above Radix dialogs/overlays so the calendar is never covered */
const PANEL_Z_INDEX = 100_000;

/** Month/year list dropdown — max height, opens downward */
const DROPDOWN_MAX_PX = 200;

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstWeekday(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

/** YYYY-MM-DD in local calendar (form / API) */
export function formatDateYmd(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** MM-DD-YYYY for display in the trigger */
export function formatDateMdy(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${m}-${d}-${y}`;
}

function parseValueToLocalDate(value: string | null | undefined): Date | null {
  if (value == null || String(value).trim() === "") return null;
  const raw = String(value).trim();
  const datePart = raw.includes("T") ? raw.split("T")[0]! : raw.slice(0, 10);
  const parts = datePart.split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts;
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function isCellAfterMax(
  day: number,
  vy: number,
  vm: number,
  maxDate: Date | undefined,
) {
  if (!maxDate) return false;
  const cell = new Date(vy, vm, day);
  const cap = new Date(
    maxDate.getFullYear(),
    maxDate.getMonth(),
    maxDate.getDate(),
  );
  return cell > cap;
}

export type CustomDatepickerProps = {
  value?: string | null;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Inclusive max selectable day (local date); omit for no limit */
  maxDate?: Date;
  className?: string;
  id?: string;
};

const CustomDatepicker: React.FC<CustomDatepickerProps> = ({
  value,
  onChange,
  placeholder = "Select date",
  disabled = false,
  maxDate,
  className,
  id,
}) => {
  const today = new Date();
  const parsed = parseValueToLocalDate(value ?? undefined);
  const [showPicker, setShowPicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(parsed);
  const [viewMonth, setViewMonth] = useState(
    parsed ? parsed.getMonth() : today.getMonth(),
  );
  const [viewYear, setViewYear] = useState(
    parsed ? parsed.getFullYear() : today.getFullYear(),
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const [monthMenuOpen, setMonthMenuOpen] = useState(false);
  const [yearMenuOpen, setYearMenuOpen] = useState(false);

  const yearOptions = useMemo(() => {
    const now = new Date();
    const maxY = maxDate ? maxDate.getFullYear() : now.getFullYear() + 10;
    const minY = 1900;
    const list: number[] = [];
    for (let y = maxY; y >= minY; y--) list.push(y);
    return list;
  }, [maxDate]);

  const onMonthSelect = (m: number) => {
    let next = m;
    if (
      maxDate &&
      viewYear === maxDate.getFullYear() &&
      m > maxDate.getMonth()
    ) {
      next = maxDate.getMonth();
    }
    setViewMonth(next);
  };

  const onYearSelect = (y: number) => {
    setViewYear(y);
    if (
      maxDate &&
      y === maxDate.getFullYear() &&
      viewMonth > maxDate.getMonth()
    ) {
      setViewMonth(maxDate.getMonth());
    }
  };

  const updatePanelPosition = useCallback(() => {
    if (!showPicker || !rootRef.current) return;
    const r = rootRef.current.getBoundingClientRect();
    const margin = 4;
    const pad = 8;
    let top = r.bottom + margin;
    let left = r.left;

    const panel = panelRef.current;
    const w = panel?.offsetWidth ?? 240;
    const h = panel?.offsetHeight ?? 300;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (left + w > vw - pad) left = Math.max(pad, vw - w - pad);
    if (left < pad) left = pad;

    if (top + h > vh - pad) top = r.top - h - margin;
    if (top < pad) top = pad;

    setPanelPos({ top, left });
  }, [showPicker]);

  useEffect(() => {
    const d = parseValueToLocalDate(value ?? undefined);
    setSelectedDate(d);
    if (d) {
      setViewMonth(d.getMonth());
      setViewYear(d.getFullYear());
    }
  }, [value]);

  useLayoutEffect(() => {
    if (!showPicker) return;
    updatePanelPosition();
    const id = requestAnimationFrame(() => updatePanelPosition());
    return () => cancelAnimationFrame(id);
  }, [showPicker, updatePanelPosition, viewMonth, viewYear]);

  useEffect(() => {
    if (!showPicker) return;
    const onScrollOrResize = () => updatePanelPosition();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [showPicker, updatePanelPosition]);

  useEffect(() => {
    if (!showPicker) {
      setMonthMenuOpen(false);
      setYearMenuOpen(false);
    }
  }, [showPicker]);

  useEffect(() => {
    if (!showPicker) return;
    const onDocDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setShowPicker(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [showPicker]);

  const daysShort = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const handleInputClick = () => {
    if (disabled) return;
    setShowPicker((o) => !o);
  };

  const handleDateClick = (day: number) => {
    if (isCellAfterMax(day, viewYear, viewMonth, maxDate)) return;
    const sel = new Date(viewYear, viewMonth, day);
    setSelectedDate(sel);
    onChange?.(formatDateYmd(sel));
    setShowPicker(false);
  };

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstWeekday = getFirstWeekday(viewYear, viewMonth);
  const calendarCells: (number | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) calendarCells.push(null);
  for (let day = 1; day <= daysInMonth; day++) calendarCells.push(day);
  while (calendarCells.length % 7 !== 0) calendarCells.push(null);

  const isSelectedDay = (day: number) =>
    !!(
      selectedDate &&
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === viewMonth &&
      selectedDate.getFullYear() === viewYear
    );

  const disableNextMonth =
    maxDate != null &&
    (viewYear > maxDate.getFullYear() ||
      (viewYear === maxDate.getFullYear() &&
        viewMonth >= maxDate.getMonth()));

  const disablePrevMonth = viewYear <= 1900 && viewMonth <= 0;

  return (
    <div
      ref={rootRef}
      id={id}
      className={cn("relative inline-block w-full max-w-full", className)}
    >
      <button
        type="button"
        disabled={disabled}
        tabIndex={0}
        onClick={handleInputClick}
        aria-haspopup="dialog"
        aria-expanded={showPicker}
        className={cn(
          "flex h-9 w-full min-w-[110px] items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-left text-sm text-foreground shadow-xs outline-none transition-[color,box-shadow]",
          "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
          disabled && "cursor-not-allowed opacity-50",
          !disabled && "cursor-pointer hover:bg-accent/40",
        )}
      >
        <span className="min-w-0 flex-1 truncate text-left">
          {selectedDate ? formatDateMdy(selectedDate) : placeholder}
        </span>
        <CalendarDays
          className="size-4 shrink-0 text-muted-foreground"
          aria-hidden
        />
      </button>
      {showPicker &&
        !disabled &&
        createPortal(
          <div
            ref={panelRef}
            className={cn(
              "pointer-events-auto isolate flex w-max min-w-[15.5rem] max-w-[min(96vw,20rem)] flex-col overflow-visible rounded-md border border-border bg-popover text-sm text-popover-foreground shadow-lg",
            )}
            style={{
              position: "fixed",
              top: panelPos.top,
              left: panelPos.left,
              zIndex: PANEL_Z_INDEX,
            }}
            role="dialog"
            aria-label="Calendar"
            aria-modal="false"
          >
            <div className="relative z-10 shrink-0 overflow-visible border-b border-border px-2 py-2">
              <div className="flex items-stretch justify-between gap-1.5">
                <button
                  type="button"
                  disabled={disablePrevMonth}
                  onClick={handlePrevMonth}
                  className="flex shrink-0 items-center rounded-md px-2 py-1.5 text-sm hover:bg-accent disabled:pointer-events-none disabled:opacity-40"
                  aria-label="Previous month"
                >
                  &lt;
                </button>
                <div className="flex min-w-0 flex-1 items-center justify-center gap-1.5">
                  <div className="relative min-w-0 flex-1">
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        setYearMenuOpen(false);
                        setMonthMenuOpen((o) => !o);
                      }}
                      className={cn(
                        "flex h-8 w-full items-center justify-between gap-1 rounded-md border border-input bg-background px-2 text-left text-xs font-medium shadow-xs sm:text-sm",
                        "hover:bg-accent/50 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
                      )}
                      aria-expanded={monthMenuOpen}
                      aria-haspopup="listbox"
                    >
                      <span className="truncate">
                        {new Date(2000, viewMonth, 1).toLocaleString(
                          "default",
                          { month: "long" },
                        )}
                      </span>
                      <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
                    </button>
                    {monthMenuOpen && (
                      <div
                        className="absolute left-0 right-0 top-full z-[2] mt-1 max-h-[200px] overflow-y-auto overscroll-contain rounded-md border border-border bg-popover text-popover-foreground shadow-md [scrollbar-gutter:stable]"
                        style={{ maxHeight: DROPDOWN_MAX_PX }}
                        onWheel={(e) => e.stopPropagation()}
                        onWheelCapture={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        tabIndex={-1}
                      >
                        <ul role="listbox" className="py-1">
                          {Array.from({ length: 12 }, (_, i) => {
                            const label = new Date(2000, i, 1).toLocaleString(
                              "default",
                              { month: "long" },
                            );
                            const isDisabled =
                              maxDate != null &&
                              viewYear === maxDate.getFullYear() &&
                              i > maxDate.getMonth();
                            const selected = i === viewMonth;
                            return (
                              <li key={i} role="presentation">
                                <button
                                  type="button"
                                  role="option"
                                  aria-selected={selected}
                                  disabled={isDisabled}
                                  onClick={() => {
                                    if (!isDisabled) {
                                      onMonthSelect(i);
                                      setMonthMenuOpen(false);
                                    }
                                  }}
                                  className={cn(
                                    "flex w-full px-3 py-1.5 text-left text-sm hover:bg-accent",
                                    selected && "bg-primary/15 font-medium",
                                    isDisabled &&
                                      "cursor-not-allowed text-muted-foreground opacity-50",
                                  )}
                                >
                                  {label}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="relative w-[4.75rem] shrink-0 sm:w-[5.25rem]">
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        setMonthMenuOpen(false);
                        setYearMenuOpen((o) => !o);
                      }}
                      className={cn(
                        "flex h-8 w-full items-center justify-between gap-0.5 rounded-md border border-input bg-background px-1.5 text-left text-xs font-medium shadow-xs sm:text-sm",
                        "hover:bg-accent/50 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
                      )}
                      aria-expanded={yearMenuOpen}
                      aria-haspopup="listbox"
                    >
                      <span className="tabular-nums">{viewYear}</span>
                      <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
                    </button>
                    {yearMenuOpen && (
                      <div
                        className="absolute left-0 right-0 top-full z-[2] mt-1 max-h-[200px] overflow-y-auto overscroll-contain rounded-md border border-border bg-popover text-popover-foreground shadow-md [scrollbar-gutter:stable]"
                        style={{ maxHeight: DROPDOWN_MAX_PX }}
                        onWheel={(e) => e.stopPropagation()}
                        onWheelCapture={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        tabIndex={-1}
                      >
                        <ul role="listbox" className="py-1">
                          {yearOptions.map((y) => {
                            const selected = y === viewYear;
                            return (
                              <li key={y} role="presentation">
                                <button
                                  type="button"
                                  role="option"
                                  aria-selected={selected}
                                  onClick={() => {
                                    onYearSelect(y);
                                    setYearMenuOpen(false);
                                  }}
                                  className={cn(
                                    "flex w-full px-3 py-1.5 text-left text-sm tabular-nums hover:bg-accent",
                                    selected && "bg-primary/15 font-medium",
                                  )}
                                >
                                  {y}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={disableNextMonth}
                  onClick={handleNextMonth}
                  className="flex shrink-0 items-center rounded-md px-2 py-1.5 text-sm hover:bg-accent disabled:pointer-events-none disabled:opacity-40"
                  aria-label="Next month"
                >
                  &gt;
                </button>
              </div>
            </div>

            <div className="max-h-[min(55dvh,14rem)] min-h-0 overflow-y-auto overscroll-contain px-2 pb-2 pt-2">
              <div
                className="grid text-center pb-1"
                style={{ gridTemplateColumns: "repeat(7, 2rem)", gap: 4 }}
              >
                {daysShort.map((d) => (
                  <div
                    key={d}
                    className="text-xs font-semibold text-muted-foreground"
                  >
                    {d}
                  </div>
                ))}
                {calendarCells.map((day, idx) => (
                  <div
                    key={idx}
                    className="flex h-8 w-8 items-center justify-center"
                  >
                    {day ? (
                      (() => {
                        const blocked = isCellAfterMax(
                          day,
                          viewYear,
                          viewMonth,
                          maxDate,
                        );
                        const selected = isSelectedDay(day);
                        return (
                          <button
                            type="button"
                            disabled={blocked}
                            onClick={() => handleDateClick(day)}
                            className={cn(
                              "flex h-8 w-8 items-center justify-center rounded-md text-sm transition-colors",
                              blocked &&
                                "cursor-not-allowed text-muted-foreground opacity-40",
                              !blocked && !selected && "hover:bg-accent",
                              selected &&
                                "bg-primary font-medium text-primary-foreground hover:bg-primary/90",
                            )}
                          >
                            {day}
                          </button>
                        );
                      })()
                    ) : (
                      <span aria-hidden>&nbsp;</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default CustomDatepicker;
